/**
 * Flat API route constants (mirrors the reference app's structure).
 *
 * ⚠️ PLACEHOLDERS — these PascalCase `.NET`-style paths are scaffolded from the
 * current feature set. Replace each value with the real backend controller/action
 * path. IDs are passed in the request body (POST-reads), not in the URL.
 */
export const API_ROUTES = {
  // ── Auth (FastAPI — base URL already includes /api/v1) ──
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_RESET_TOKEN: '/auth/reset-password/verify',
  VERIFY_INVITE_TOKEN: '/auth/invite/verify',
  MFA_VERIFY: '/auth/mfa/verify',
  MFA_RESEND: '/auth/mfa/resend',

  // ── Users ──
  GET_USERS: '/superadmin/users/list',
  GET_USERS_KPIS: '/superadmin/users/kpis',
  GET_USER_BY_ID: '/superadmin/users/detail',
  ADD_USER: '/superadmin/users',
  UPDATE_USER: '/superadmin/users',
  DELETE_USER: '/superadmin/users',
  UPDATE_USER_STATUS: '/superadmin/users/status',
  RESEND_INVITATION_LINK: '/superadmin/users/resend-invitation',

  // ── Roles & permissions ──
  GET_ROLES: '/superadmin/roles/list',
  /** Lightweight role list for <select> dropdowns — GET, no body, no paging. */
  GET_ROLES_DROPDOWN: '/superadmin/roles/dropdown',
  GET_ROLE_BY_ID: '/superadmin/roles/detail',
  GET_PERMISSIONS: '/superadmin/permissions',
  ADD_ROLE: '/superadmin/roles',
  UPDATE_ROLE: '/superadmin/roles',
  UPDATE_ROLE_STATUS: '/superadmin/roles/status',
  DELETE_ROLE: '/superadmin/roles',

  // ── Organizations ──
  GET_ORGANIZATIONS: '/superadmin/organizations/list',
  GET_ORGANIZATION_BY_ID: '/superadmin/organizations/detail',
  ADD_ORGANIZATION: '/superadmin/organizations',
  UPDATE_ORGANIZATION: '/AdminManageOrganization/UpdateOrganization',
  DELETE_ORGANIZATION: '/superadmin/organizations/delete',
  UPDATE_ORGANIZATION_STATUS: '/superadmin/organizations/status',
  /** Stage options for the org list's Stage column — GET, no body. */
  GET_ORG_STATUS_DROPDOWN: '/superadmin/organizations/status/dropdown',

  // ── Masters (Org Masters Data — Super Admin, view-only) ──
  /** BHS Matrix records for an org — POST { org_uuid, page, limit, search, sort_by, order }. */
  GET_MASTER_BHS: '/superadmin/masters/bhs-matrix/list',
  /** One BHS Matrix record — POST { org_uuid, id }. */
  GET_MASTER_BHS_DETAIL: '/superadmin/masters/bhs-matrix/view',
  /** CPT-to-Credential records for an org — POST { org_uuid, page, limit, search, license, sort_by, order }. */
  GET_MASTER_CREDENTIALS: '/superadmin/masters/cpt-credentials/list',

  // ── Audit logs ──
  // Security policy (progressive lockout config) — GET reads, PUT patches
  GET_SECURITY_POLICY: '/superadmin/security-policy',
  UPDATE_SECURITY_POLICY: '/superadmin/security-policy',

  GET_AUDIT_LOGS: '/superadmin/audit-logs/list',
  GET_AUDIT_LOG_FILTERS: '/superadmin/audit-logs/filters',
  EXPORT_AUDIT_LOGS: '/superadmin/audit-logs/export',
  VERIFY_AUDIT_LOGS: '/superadmin/audit-logs/verify',

  // ── Dashboard ──

  GET_DASHBOARD_ORGANIZATIONS: '/superadmin/dashboard/organizations',
  GET_DASHBOARD_METRICS: '/superadmin/dashboard/metrics',

  // ── Profile ──
  GET_PROFILE: '/auth/profile',
  UPDATE_PROFILE: '/superadmin/users/profile',
  CHANGE_PASSWORD: '/auth/update-password',
  UPDATE_PROFILE_PICTURE: '/superadmin/users/profile-picture',

  // ── Common / masters lookups ──
  GET_COMMON_LIST: '/Common/GetAllMasterStatusList',
  GET_MASTER_LIST: '/meta/enums',
} as const;
